import { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import AppNavBar from "../components/AppNavBar";
import { useTheme } from "../useTheme";
import type { User } from "@/types/app";

export default function Profile() {
  const { user, handleSignOut, loading } = useAuth();
  const [formData, setFormData] = useState<User>({});
  const { theme, toggleTheme } = useTheme();
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  useEffect(() => {
    setFormData({ ...user, permissions: [] });
  }, [user]);

  return (
    <div className="min-vh-100 d-flex flex-column">
      {/* Navbar */}
      <AppNavBar title={'Profile Details'} />
      {/* Main Content */}
      <Container fluid className="flex-grow-1 mt-5 py-4 px-5">
        {/* Account Details */}
        <Card className="border border-border p-3">
          <h5 className="fw-semibold text-secondary mb-3">Account Details</h5>
          <div className="d-flex gap-3 flex-wrap rounded">
            <Form>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group controlId="name">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name here"
                      readOnly
                      required
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="fc_no">
                    <Form.Label>Family Code Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="fc_no"
                      value={formData.fc_no?.toString()?.replace(/\D+/g, "")}
                      onChange={handleChange}
                      placeholder="Enter your family code here"
                      readOnly
                      required
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="role">
                    <Form.Label>User Role</Form.Label>
                    <Form.Control
                      type="text"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      readOnly
                      required
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="username">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="Enter your username here"
                      readOnly
                      required
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="email">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="text"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email here"
                      readOnly
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>
        </Card>

        {/* Account Preferences */}
        <Card className="mt-3 border border-border p-3">
          <h5 className="fw-semibold text-secondary mb-3">
            Account Preferences
          </h5>
          <div className="d-flex flex-column flex-md-row border border-border justify-content-between align-items-md-center gap-3 p-3 rounded-3">
            <div>
              <h6 className="mb-1 text-gray-600">Platform Theme</h6>
              <small className="text-subtitle">
                Switch between light and dark mode
              </small>
            </div>
            <div
              className="d-flex align-items-center justify-content-end gap-2"
              style={{ cursor: "pointer" }}
              onClick={toggleTheme}
            >
              {/* Light Tag */}
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: theme === "dark" ? "#adb5bd" : "#ffc107",
                  transition: "0.3s",
                  userSelect: "none",
                }}
              >
                Light
              </span>

              {/* Switch */}
              <div
                className={`custom-toggle ${theme === "dark" ? "active" : ""}`}
                role="switch"
                aria-checked={theme === "dark"}
                onClick={toggleTheme}
              >
                <div className="toggle-circle">
                  {theme === "dark" ? "🌙" : "☀️"}
                </div>
              </div>

              {/* Dark Tag */}
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: theme === "dark" ? "#0dcaf0" : "#adb5bd",
                  transition: "0.3s",
                  userSelect: "none",
                }}
              >
                Dark
              </span>
            </div>
          </div>
        </Card>

        {/* Account Actions */}
        <Card className="mt-3 border border-border p-3">
          <h5 className="fw-semibold text-secondary mb-3">Account Actions</h5>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 p-3 rounded-3">
            <div>
              <h6 className="mb-1 fw-semibold text-danger">Sign Out</h6>
              <small className="text-gray-300">
                You will be securely logged out from your current session on
                this device.
              </small>
            </div>

            <div className="d-flex gap-2">
              <Button variant="danger" onClick={handleSignOut}>
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Signing Out...
                  </>
                ) : (
                  "Sign Out"
                )}
              </Button>
            </div>
          </div>
        </Card>
      </Container>
    </div>
  );
}
